import { useEffect, useRef, useState } from 'react'
import { BusFront } from 'lucide-react'
import {
  getShuttleRouteMotionMode,
  getShuttleRouteMotionPhase,
} from '../../utils/shuttleRouteMotion'
import { HOME_SHUTTLE_APPROACHING_MAX_MINUTES } from '../../utils/homeTimePolicy'
import './ShuttleRouteMotion.css'

const clampBusCount = value => {
  const count = Number(value)
  return Number.isFinite(count) ? Math.max(1, Math.min(3, Math.round(count))) : 1
}

const timeToMinutes = value => {
  const [hours, minutes] = String(value ?? '').split(':').map(Number)
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : null
}

export default function ShuttleRouteMotion({
  from = '병점역',
  to = '학교',
  departureTime = null,
  minsUntilDeparture = null,
  busCount = 1,
  followingDepartureTime = null,
  visualPhase,
  mode,
  isEnded = false,
  isDark = false,
}) {
  const phase = visualPhase ?? getShuttleRouteMotionPhase({
    departureTime,
    minsUntilDeparture,
    isEnded,
  })
  const motionMode = mode ?? getShuttleRouteMotionMode({ departureTime, followingDepartureTime })
  const count = clampBusCount(busCount)
  const previousDepartureRef = useRef(departureTime)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isHandoffPending, setIsHandoffPending] = useState(false)

  useEffect(() => {
    const previousDeparture = previousDepartureRef.current
    previousDepartureRef.current = departureTime
    if (!previousDeparture || !departureTime || previousDeparture === departureTime) return undefined

    setIsTransitioning(true)
    const timeoutId = window.setTimeout(() => setIsTransitioning(false), 420)
    return () => window.clearTimeout(timeoutId)
  }, [departureTime])

  useEffect(() => {
    setIsHandoffPending(false)
    if (phase !== 'departing' || !followingDepartureTime) return undefined

    let settleTimeoutId
    const handoffTimeoutId = window.setTimeout(() => {
      setIsHandoffPending(true)
      setIsTransitioning(true)
      settleTimeoutId = window.setTimeout(() => setIsTransitioning(false), 420)
    }, 2600)

    return () => {
      window.clearTimeout(handoffTimeoutId)
      if (settleTimeoutId) window.clearTimeout(settleTimeoutId)
    }
  }, [followingDepartureTime, phase])

  const departureMin = timeToMinutes(departureTime)
  const followingMin = timeToMinutes(followingDepartureTime)
  const handoffGap = departureMin != null && followingMin != null ? followingMin - departureMin : null
  const handoffPhase = handoffGap != null && handoffGap > 0 && handoffGap <= HOME_SHUTTLE_APPROACHING_MAX_MINUTES ? 'approaching' : 'idle'
  const renderedPhase = isTransitioning ? 'transitioning' : isHandoffPending ? handoffPhase : phase

  return (
    <div
      className={`srm-stage${isDark ? ' is-dark' : ''}`}
      data-phase={renderedPhase}
      data-mode={motionMode}
      data-bus-count={count}
      data-transitioning={isTransitioning ? 'true' : 'false'}
      aria-hidden="true"
    >
      <div className="srm-labels">
        <span className="srm-label srm-label-from">{from}</span>
        <span className="srm-label srm-label-to">{to}</span>
      </div>

      <div className="srm-rail">
        <span className="srm-rail-line" />
        <span className="srm-stop srm-stop-start" />
        <span className="srm-stop srm-stop-end" />

        {Array.from({ length: count }, (_, index) => (
          <span
            className="srm-cluster-pulse"
            key={index}
            style={{ '--srm-pulse-delay': `${index * 240}ms` }}
          />
        ))}

        <span className="srm-bus">
          <span className="srm-bus-shell">
            <BusFront size={16} strokeWidth={2.1} />
          </span>
        </span>
      </div>
    </div>
  )
}
